/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    this.listId = this.dataset.klaviyoListId;
    this.companyId = this.dataset.klaviyoCompanyId;
    this.customSource = this.dataset.klaviyoCustomSource || '';

    if (this.listId && this.companyId) {
      const form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleKlaviyoSubmit(e));
      }
    }

    this.scheduleMessageDismiss();
  }

  scheduleMessageDismiss() {
    const visible = Array.from(this.querySelectorAll('.email-signup__message')).filter(
      (el) => !el.hidden && el.offsetParent !== null
    );
    if (visible.length === 0) return;
    setTimeout(() => {
      visible.forEach((el) => {
        el.hidden = true;
        el.style.display = 'none';
      });
    }, 5000);
  }

  async handleKlaviyoSubmit(e) {
    e.preventDefault();
    const emailInput = this.querySelector('.email-signup__input');
    if (!emailInput || !emailInput.value.trim()) return;
    const email = emailInput.value.trim();

    this.hideKlaviyoMessages();

    const attributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: { email },
        },
      },
    };
    if (this.customSource) {
      attributes.custom_source = this.customSource;
    }

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            revision: '2024-10-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes,
              relationships: {
                list: {
                  data: { type: 'list', id: this.listId },
                },
              },
            },
          }),
        }
      );

      if (res.ok || res.status === 202) {
        this.showKlaviyoSuccess();
        emailInput.value = '';
      } else {
        const data = await res.json().catch(() => null);
        const detail = data && data.errors && data.errors[0] && data.errors[0].detail;
        this.showKlaviyoError(detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      this.showKlaviyoError('Network error. Please try again.');
    }
  }

  hideKlaviyoMessages() {
    this.querySelectorAll('.email-signup__message--klaviyo').forEach((el) => {
      el.hidden = true;
    });
  }

  showKlaviyoSuccess() {
    const el = this.querySelector('[data-klaviyo-success]');
    if (!el) return;
    el.hidden = false;
    el.focus();
    setTimeout(() => {
      el.hidden = true;
    }, 5000);
  }

  showKlaviyoError(msg) {
    const el = this.querySelector('[data-klaviyo-error]');
    if (!el) return;
    const textEl = el.querySelector('[data-klaviyo-error-text]');
    if (textEl) textEl.textContent = msg;
    el.hidden = false;
    el.focus();
    setTimeout(() => {
      el.hidden = true;
    }, 5000);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
